import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import FileUpload from '../../components/common/FileUpload';
import CountrySelector from '../../components/common/CountrySelector';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nContext';
import { toast, ToastContainer } from 'react-toastify';

function EditUser() {
    const { t } = useI18n();
    const { type, id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        email: '',
        firstname: '',
        lastname: '',
        enterpriseName: '',
        telephone: '',
        country: '',
        password: '',
        active: true,
        premium: false
    });

    const [logo, setLogo] = useState(null);
    const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
    const [logoRemoved, setLogoRemoved] = useState(false);

    useEffect(() => {
        fetchUser();
    }, [type, id]);

    const fetchUser = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/admin/users/${type}/${id}`);
            const user = response.data;
            setFormData({
                email: user.email || '',
                firstname: user.firstname || '',
                lastname: user.lastname || '',
                enterpriseName: user.enterpriseName || '',
                telephone: user.telephone || '',
                country: user.country || '',
                password: '',
                active: user.active !== false,
                premium: user.premium || false
            });
            setCurrentLogoUrl(user.logoUrl);
            setError(null);
        } catch (err) {
            setError(err.message || t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type: fieldType, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: fieldType === 'checkbox' ? checked : value
        }));
    };

    const handleCountryChange = (countryCode) => {
        setFormData(prev => ({ ...prev, country: countryCode }));
    };

    const handleLogoChange = (newFile) => {
        setLogo(newFile);
        if (newFile === null) {
            setLogoRemoved(true);
        } else if (newFile instanceof File) {
            setLogoRemoved(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('email', formData.email);
            formDataToSend.append('firstname', formData.firstname);
            formDataToSend.append('lastname', formData.lastname);
            formDataToSend.append('enterpriseName', formData.enterpriseName);
            formDataToSend.append('telephone', formData.telephone);
            formDataToSend.append('active', formData.active);
            if (type === 'expediteur') {
                formDataToSend.append('premium', formData.premium);
            }
            if (formData.country) formDataToSend.append('country', formData.country);
            if (formData.password) formDataToSend.append('password', formData.password);
            if (logoRemoved) formDataToSend.append('removeLogo', 'true');
            if (logo) formDataToSend.append('logo', logo);

            await api.put(`/admin/users/${type}/${id}`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success(t('common.success'));
            setLogo(null);

            setTimeout(() => {
                navigate('/admin/users');
            }, 1000);
        } catch (err) {
            setError(err.message || t('common.error'));
            toast.error(err.message || t('common.error'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="d-flex justify-content-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <ToastContainer />
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 className="h3 mb-1 fw-bold text-dark">{t('admin.editUser.title')}</h1>
                        <p className="text-muted small mb-0">{formData.enterpriseName} ({type})</p>
                    </div>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/admin/users')}
                    >
                        <i className="bi bi-arrow-left me-2"></i>
                        {t('common.back')}
                    </button>
                </div>

                {error && (
                    <div className="alert alert-danger shadow-sm border-0 mb-4" style={{ borderRadius: '0.75rem' }}>
                        <i className="bi bi-exclamation-triangle me-2"></i>{error}
                    </div>
                )}

                <div className="card border-0 shadow-sm" style={{ borderRadius: '0.9375rem' }}>
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('register.form.firstName')} *</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-lg"
                                        style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                                        name="firstname"
                                        value={formData.firstname}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('register.form.lastName')} *</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-lg"
                                        style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                                        name="lastname"
                                        value={formData.lastname}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('register.form.email')} *</label>
                                    <input
                                        type="email"
                                        className="form-control form-control-lg"
                                        style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('register.form.telephone')} *</label>
                                    <input
                                        type="tel"
                                        className="form-control form-control-lg"
                                        style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                                        name="telephone"
                                        value={formData.telephone}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('register.form.enterpriseName')} *</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-lg"
                                        style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                                        name="enterpriseName"
                                        value={formData.enterpriseName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('admin.userList.table.country')}</label>
                                    <CountrySelector
                                        value={formData.country}
                                        onChange={handleCountryChange}
                                        className="form-select-lg"
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-semibold">{t('register.form.password')}</label>
                                    <input
                                        type="password"
                                        className="form-control form-control-lg"
                                        style={{ backgroundColor: '#f8fafc', borderRadius: '0.625rem' }}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder={t('admin.editUser.passwordPlaceholder') || "Laissez vide pour conserver le mot de passe actuel"}
                                    />
                                </div>

                                <div className="col-12">
                                    <div className="d-flex gap-4 p-3 bg-light rounded-3">
                                        <div className="form-check form-switch cursor-pointer">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="activeToggle"
                                                name="active"
                                                checked={formData.active}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label fw-bold" htmlFor="activeToggle">
                                                {t('admin.editUser.activationToggle')}
                                            </label>
                                        </div>

                                        {type === 'expediteur' && (
                                            <div className="form-check form-switch cursor-pointer">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="premiumToggle"
                                                    name="premium"
                                                    checked={formData.premium}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label fw-bold" htmlFor="premiumToggle">
                                                    {t('admin.editUser.premiumToggle')}
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {type === 'transporteur' && (
                                    <div className="col-12">
                                        <label className="form-label fw-bold mb-3">{t('vehicles.documents.existing')}</label>
                                        <FileUpload
                                            id="logo-upload"
                                            label={t('common.profile')}
                                            value={logo || (logoRemoved ? null : currentLogoUrl)}
                                            onChange={handleLogoChange}
                                            maxSizeMB={2}
                                            accept="image/png, image/jpeg, image/jpg"
                                        />
                                    </div>
                                )}

                                <div className="col-12 pt-3 border-top">
                                    <div className="d-flex gap-3">
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-lg px-5 shadow-sm"
                                            style={{ borderRadius: '0.75rem' }}
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                            ) : (
                                                <i className="bi bi-check2-circle me-2"></i>
                                            )}
                                            {t('common.save')}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-lg px-4"
                                            style={{ borderRadius: '0.75rem' }}
                                            onClick={() => navigate('/admin/users')}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default EditUser;
